<template>
  <div :class="wrapperClass" :style="wrapperStyle">
    <div ref="container" class="tp-link-video__container"></div>
  </div>
</template>

<script>
export default {
  name: 'TpLinkVideo',
  props: {
    appKey: { type: String, required: true },
    appSecret: { type: String, required: true },
    url: { type: String, required: true },
    /** video=预览  sdvod=回放 */
    streamType: { type: String, default: 'video' }, // 'video' | 'sdvod'
    /** 协议：多数场景传 'relay' */
    type: { type: String, default: 'relay' },

    /** 是否自动播放、静音、循环 */
    autoplay: { type: Boolean, default: true },
    muted: { type: Boolean, default: true },
    loop: { type: Boolean, default: false },

    /** 初始音量 0~1 */
    volume: { type: Number, default: 1 },

    /** 回放：开始/结束时间（时间戳或 ISO 字符串，取决于 SDK） */
    playbackStart: { type: [String, Number, Date], default: null },
    playbackEnd:   { type: [String, Number, Date], default: null },

    /** 画面与渲染 */
    poster: { type: String, default: '' },
    useWebGL: { type: Boolean, default: true },
    objectFit: { type: String, default: 'contain' }, // contain/cover/fill

    options: { type: Object, default: () => ({}) },

    /** 外层容器样式/类名 */
    wrapperClass: { type: String, default: 'tp-link-video' },
    wrapperStyle: { type: [String, Object], default: '' }
  },

  data () {
    return {
      player: null
    }
  },

  mounted () {
    this.initPlayer()
  },

  beforeDestroy () {
    this.destroy()
  },

  watch: {
    // 这些关键入参变化时，重建实例最稳妥
    url () { this.reinit() },
    streamType () { this.reinit() },
    type () { this.reinit() },
    useWebGL () { this.reinit() },
    // 运行期可动态调的字段
    volume (v) { this.setVolume(v) },
    muted (m) { this.setMuted(m) }
  },

  methods: {
    getTumsCtor () {
      const g = window
      return g.TumsPlayer || (g['tums-player'] && g['tums-player'].default) || null
    },

    initPlayer () {
      const Ctor = this.getTumsCtor()
      if (!Ctor) {
        console.error('[TpLinkVideo] TumsPlayer not found on window.')
        return
      }

      const baseOpts = {
        appKey: this.appKey,
        appSecret: this.appSecret,
        url: this.url,
        type: this.type,            // 'relay' | 'rtsp'（如有）
        streamType: this.streamType, // 'video' | 'sdvod'
        pluginPath: '/nnjt_charging_pile_screen',
        autoplay: this.autoplay,
        muted: this.muted,
        loop: this.loop,
        volume: this.volume,
        poster: this.poster,
        webgl: this.useWebGL,
        objectFit: this.objectFit
      }

      // 回放时间（若提供）
      if (this.streamType === 'sdvod' && (this.playbackStart || this.playbackEnd)) {
        baseOpts.playback = {
          start: this.playbackStart,
          end: this.playbackEnd
        }
      }

      const opts = Object.assign({}, this.options || {}, baseOpts)

      // 实例化
      this.player = new Ctor(this.$refs.container, opts)

      // 绑定常用事件（按需增删）
      const on = (evt, fn) => this.player && this.player.on && this.player.on(evt, fn)
      on('ready',        (e) => this.$emit('ready', e))
      on('play',         (e) => this.$emit('play', e))
      on('pause',        (e) => this.$emit('pause', e))
      on('playing',      (e) => this.$emit('playing', e))
      on('ended',        (e) => this.$emit('ended', e))
      on('disconnected', (e) => this.$emit('disconnected', e))
      on('stream',       (e) => this.$emit('stream', e))
      on('zoom',         (e) => this.$emit('zoom', e))
      on('videoRecordFinished', () => this.$emit('videoRecordFinished'))
      on('warning',      (e) => this.$emit('warning', e))
      on('error',        (err) => this.$emit('error', err))
    },

    reinit () {
      this.destroy()
      this.$nextTick(this.initPlayer)
    },
    destroy () {
      if (this.player && this.player.destroy) {
        try { this.player.destroy() } catch (e) { /* ignore */ }
      }
      this.player = null
    },
    // ===== 对外方法 =====
    play () {
      return this.player && this.player.play && this.player.play()
    },
    pause () {
      return this.player && this.player.pause && this.player.pause()
    },
    stop () {
      return this.player && this.player.stop && this.player.stop()
    },
    snapshot (fileName) {
      // 有的 SDK 是 snapshot()；有的是 capture() 或 screenShot()
      const p = this.player
      if (!p) return
      if (p.snapshot) return p.snapshot(fileName)
      if (p.capture) return p.capture(fileName)
      if (p.screenShot) return p.screenShot(fileName)
    },
    setVolume (v) {
      if (this.player && this.player.setVolume) this.player.setVolume(v)
    },
    setMuted (m) {
      if (this.player) {
        if (this.player.setMuted) this.player.setMuted(m)
        else if (this.player.mute) m ? this.player.mute() : this.player.unmute && this.player.unmute()
      }
    },
    enterFullscreen () {
      if (this.player && this.player.requestFullscreen) return this.player.requestFullscreen()
      const el = this.$refs.container
      const req = el.requestFullscreen || el.webkitRequestFullscreen || el.mozRequestFullScreen || el.msRequestFullscreen
      return req && req.call(el)
    },
    exitFullscreen () {
      const d = document
      const exit = d.exitFullscreen || d.webkitExitFullscreen || d.mozCancelFullScreen || d.msExitFullscreen
      return exit && exit.call(d)
    },
    /** 回放窗口更新（仅 streamType='sdvod' 有效） */
    setPlaybackWindow (start, end) {
      if (this.player && this.player.setPlaybackWindow) {
        return this.player.setPlaybackWindow({ start, end })
      }
    },
    /** PTZ 示例（具体方法名依 SDK 而定） */
    ptz (dir, speed = 1) {
      if (!this.player) return
      if (this.player.ptz) return this.player.ptz(dir, speed)    // 如: 'up' | 'down' | 'left' | 'right' | 'zoomIn' | 'zoomOut'
      if (this.player.controlPTZ) return this.player.controlPTZ(dir, speed)
    },
    /** 万能代理：this.$refs.player.call('realMethod', arg1, arg2) */
    call (method, ...args) {
      const fn = this.player && this.player[method]
      if (typeof fn === 'function') return fn.apply(this.player, args)
      console.warn(`[TpLinkVideo] method not found: ${method}`)
    }
  }
}
</script>

<style scoped>
.tp-link-video__container {
  width: 100%;
  height: 100%;
  background: #000;
}
</style>